/**
 * Translated labels for the registry's enum values. Every key is spelled out as
 * a literal so the i18n coverage guard can see it.
 */

type T = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

export function categoryLabel(t: T, category: string): string {
  switch (category) {
    case 'member':
      return t('templateVariables.category.member', 'Member');
    case 'organization':
      return t('templateVariables.category.organization', 'Organization');
    case 'staff':
      return t('templateVariables.category.staff', 'Staff');
    case 'program':
      return t('templateVariables.category.program', 'Program');
    case 'links':
      return t('templateVariables.category.links', 'Links');
    case 'service':
      return t('templateVariables.category.service', 'Service');
    case 'appointment':
      return t('templateVariables.category.appointment', 'Appointment');
    case 'compliance':
      return t('templateVariables.category.compliance', 'Compliance');
    case 'general':
      return t('templateVariables.category.general', 'General');
    case 'custom_field':
      return t('templateVariables.category.customField', 'Custom fields');
    case 'custom':
      return t('templateVariables.category.custom', 'Custom');
    default:
      return category ? category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, ' ') : '';
  }
}

export function scopeLabel(t: T, scope: string): string {
  switch (scope) {
    case 'system':
      return t('templateVariables.scope.system', 'System');
    case 'org':
      return t('templateVariables.scope.org', 'Organization');
    case 'custom_field':
      return t('templateVariables.scope.customField', 'Custom field');
    default:
      return scope;
  }
}

export function sourceKindLabel(t: T, kind: string): string {
  switch (kind) {
    case 'static':
      return t('templateVariables.sourceKind.static', 'Fixed value');
    case 'member_field':
      return t('templateVariables.sourceKind.memberField', 'Member field');
    case 'program_field':
      return t('templateVariables.sourceKind.programField', 'Program field');
    case 'custom_field':
      return t('templateVariables.sourceKind.customField', 'Custom field');
    case 'input':
      return t('templateVariables.sourceKind.input', 'Entered by staff when sending');
    case 'system':
      return t('templateVariables.sourceKind.system', 'Built in');
    default:
      return kind;
  }
}

export function valueTypeLabel(t: T, type: string): string {
  switch (type) {
    case 'text':
      return t('templateVariables.valueType.text', 'Text');
    case 'date':
      return t('templateVariables.valueType.date', 'Date');
    case 'datetime':
      return t('templateVariables.valueType.datetime', 'Date and time');
    case 'number':
      return t('templateVariables.valueType.number', 'Number');
    case 'link':
      return t('templateVariables.valueType.link', 'Link');
    case 'select':
      return t('templateVariables.valueType.select', 'Choice from a list');
    case 'boolean':
      return t('templateVariables.valueType.boolean', 'Yes / no');
    default:
      return type;
  }
}

export function fallbackLabel(t: T, fallback: string): string {
  switch (fallback) {
    case 'block':
      return t('templateVariables.fallback.block', 'Block the send');
    case 'default':
      return t('templateVariables.fallback.default', 'Use the default value');
    case 'empty':
      return t('templateVariables.fallback.empty', 'Leave it blank');
    default:
      return fallback;
  }
}

export function appliesToLabel(t: T, appliesTo: string): string {
  switch (appliesTo) {
    case 'messages':
      return t('templateVariables.appliesTo.messages', 'Messages');
    case 'forms':
      return t('templateVariables.appliesTo.forms', 'Forms');
    case 'both':
      return t('templateVariables.appliesTo.both', 'Messages and forms');
    default:
      return appliesTo;
  }
}

export function reasonLabel(t: T, reason: string): string {
  switch (reason) {
    case 'unknown_variable':
      return t('templateVariables.reason.unknownVariable', 'Not a variable this organization has defined');
    case 'no_value':
      return t('templateVariables.reason.noValue', 'No value for this member');
    case 'needs_context':
      return t('templateVariables.reason.needsContext', 'Needs more context than this send has');
    case 'needs_input':
      return t('templateVariables.reason.needsInput', 'Needs a value entered by staff');
    case 'invalid_input':
      return t('templateVariables.reason.invalidInput', 'The value entered is not valid');
    case 'inactive':
      return t('templateVariables.reason.inactive', 'This variable is turned off');
    case 'not_available_here':
      return t('templateVariables.reason.notAvailableHere', 'Not available in this kind of template');
    default:
      return reason;
  }
}

export function memberFieldLabel(t: T, field: string): string {
  switch (field) {
    case 'first_name':
      return t('templateVariables.memberField.firstName', 'First name');
    case 'last_name':
      return t('templateVariables.memberField.lastName', 'Last name');
    case 'email':
      return t('templateVariables.memberField.email', 'Email');
    case 'phone_no':
      return t('templateVariables.memberField.phone', 'Phone');
    case 'street_address':
      return t('templateVariables.memberField.streetAddress', 'Street address');
    case 'address_line2':
      return t('templateVariables.memberField.addressLine2', 'Address line 2');
    case 'city':
      return t('templateVariables.memberField.city', 'City');
    case 'state':
      return t('templateVariables.memberField.state', 'State');
    case 'zip_code':
      return t('templateVariables.memberField.zipCode', 'ZIP code');
    case 'county':
      return t('templateVariables.memberField.county', 'County');
    case 'preferred_language':
      return t('templateVariables.memberField.preferredLanguage', 'Preferred language');
    case 'member_number':
      return t('templateVariables.memberField.memberNumber', 'Member number');
    case 'medicaid_id':
      return t('templateVariables.memberField.medicaidId', 'Medicaid ID');
    case 'dob':
      return t('templateVariables.memberField.dob', 'Date of birth');
    default:
      return field;
  }
}

export function programFieldLabel(t: T, field: string): string {
  switch (field) {
    case 'name':
      return t('templateVariables.programField.name', 'Program name');
    case 'description':
      return t('templateVariables.programField.description', 'Description');
    case 'program_type':
      return t('templateVariables.programField.programType', 'Program type');
    case 'program_manager_name':
      return t('templateVariables.programField.managerName', 'Program manager name');
    case 'program_manager_email':
      return t('templateVariables.programField.managerEmail', 'Program manager email');
    case 'program_manager_phone':
      return t('templateVariables.programField.managerPhone', 'Program manager phone');
    case 'start_date':
      return t('templateVariables.programField.startDate', 'Start date');
    case 'end_date':
      return t('templateVariables.programField.endDate', 'End date');
    default:
      return field;
  }
}

/** Ordered categories for grouping; unknown ones sort after these, alphabetically. */
export const CATEGORY_ORDER = [
  'member',
  'organization',
  'staff',
  'program',
  'appointment',
  'service',
  'links',
  'general',
  'compliance',
  'custom',
  'custom_field',
];

export function sortCategories(categories: string[]): string[] {
  return [...categories].sort((a, b) => {
    const ia = CATEGORY_ORDER.indexOf(a);
    const ib = CATEGORY_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}
